import type { MoreLinkArg } from "@fullcalendar/core"
import { App, Button, Flex, Modal, Skeleton, Spin, Typography } from "antd"
import React, { Suspense, lazy, useCallback, useEffect } from "react"
import { useTranslation } from "react-i18next"

import { useAppDispatch } from "../../../hooks/use-app-dispatch.ts"
import { useAppSelector } from "../../../hooks/use-app-selector.ts"
import { useLatestRequestId } from "../../../hooks/use-latest-request-id.ts"
import { useRefetchOnFocus } from "../../../hooks/use-refetch-on-focus.ts"
import { getEventDetails } from "../api/services.ts"
import { calendarSetProp, replaceSelectedEvent } from "../redux/calendar.slice.ts"
import {
  createCalendarEvent,
  deleteCalendarEvent,
  partialUpdateCalendarEvent,
  updateReminderStatus
} from "../redux/calendar.thunks.ts"
import type {
  CalendarModalData,
  CalendarState
} from "../redux/calendar.types.ts"
import type { CalendarPermissions, Event } from "../types/types.ts"

const CalendarAddForm = lazy(
  () => import("./components/CalendarAddForms/CalendarAddForm.tsx")
)

const CalendarView = lazy(
  () => import("./components/CalendarView/CalendarView.tsx")
)

const CalendarMoreEventsModal = lazy(
  () => import("./components/CalendarMoreEvents/CalendarMoreEventsModal.tsx")
)

const { Text } = Typography

interface CalendarPageActionsProps {
  dateFormat: string | null
  timezone: string
  userId: string
  permissions: CalendarPermissions
  // Owned by CalendarPage.tsx: it's the one place an event gets opened from
  // (grid click, "+N more", and a Lead's deep-link all call the same
  // handler there), so the loading/error state lives with it rather than
  // being duplicated — and inconsistent — here.
  onSelectEvent: (id: string | null) => void
  openingEventId: string | null
  failedEventId: string | null
  onRetryEventDetails: () => void
  onDismissEventError: () => void
  /** The deep-linked event id and whether it asked to open straight into
   *  edit mode — scoped by id below so a later, unrelated event selection
   *  (grid click, "+N more") never inherits a stale `?edit=1`. */
  focusEventId?: string | null
  focusEditMode?: boolean
  /** Fired when the edit form's own Cancel is clicked — the event stays
   *  open, only the deep link's edit intent is spent. */
  onEditCancelled?: () => void
}

export default function CalendarPageActions({
  dateFormat,
  timezone,
  userId,
  permissions,
  onSelectEvent,
  openingEventId,
  failedEventId,
  onRetryEventDetails,
  onDismissEventError,
  focusEventId = null,
  focusEditMode = false,
  onEditCancelled
}: CalendarPageActionsProps) {
  const { modal } = App.useApp()
  const { t } = useTranslation()
  const dispatch = useAppDispatch()

  const moreEventsData = useAppSelector(
    (state) => state.calendar?.moreEventsData
  )
  const addModalData = useAppSelector((state) => state.calendar?.addModalData)
  const selectedEvent = useAppSelector((state) => state.calendar?.selectedEvent)
  const saveFetching = useAppSelector(
    (state) => state.calendar?.saveFetching || false
  )
  const fetching = useAppSelector((state) => state.calendar?.fetching || false)

  const startInEditMode = focusEditMode && selectedEvent?.id === focusEventId

  const handleSetProp = useCallback(
    (key: keyof CalendarState, value: CalendarState[keyof CalendarState]) => {
      dispatch(calendarSetProp({ key, value }))
    },
    [dispatch]
  )

  // An RSVP is answered from the invite email's Accept/Decline link — a
  // separate, unauthenticated tab this app's own request cycle can't see —
  // so a Quick View left open across that click keeps showing the answer it
  // had when it opened. Catch it up when the tab regains focus.
  const selectedEventId = selectedEvent?.id
  // Invalidates a request still in flight for whichever event was open when
  // it fired, so a slow response can't land after the user closed the Quick
  // View, moved on to a different event, or (see below) a real fetch of its
  // own has taken over.
  const { claim, isCurrent, invalidate } = useLatestRequestId()
  useEffect(() => {
    invalidate()
  }, [selectedEventId, invalidate])
  // `handleSelectEvent`'s own real fetchEventDetails(id) fetch — opening a
  // different event, or re-opening this same one — sets `fetching` before
  // `selectedEvent` itself changes (that only happens once *it* resolves,
  // on fetchEventDetails.fulfilled), so the effect above alone would miss
  // it entirely: `selectedEventId` never moves during exactly the window a
  // background response racing that real fetch needs caught in. A real
  // fetch starting invalidates unconditionally, whatever event it's for —
  // it's the one signal this component has for "something authoritative is
  // now in charge of `selectedEvent`."
  useEffect(() => {
    if (fetching) invalidate()
  }, [fetching, invalidate])
  // A request that outlives this component (navigating off the Calendar
  // page while a refresh is in flight) must not dispatch into the still-live
  // store on the way out.
  useEffect(() => () => invalidate(), [invalidate])

  const refreshSelectedEvent = useCallback(() => {
    if (!selectedEventId) return
    const requestId = claim()
    getEventDetails(selectedEventId).then((response) => {
      if (!isCurrent(requestId)) return
      const data = response.data as unknown as Event | undefined
      if (response.ok && data?.id) {
        dispatch(replaceSelectedEvent(data))
      }
    })
  }, [selectedEventId, dispatch, claim, isCurrent])

  useRefetchOnFocus(refreshSelectedEvent, !!selectedEventId)

  const handleSetModalData = useCallback(
    (data: CalendarModalData | null) => {
      handleSetProp("addModalData", data)
    },
    [handleSetProp]
  )

  const handleSetMoreEventsData = useCallback(
    (data?: MoreLinkArg) => {
      if (data) {
        handleSetProp("moreEventsData", {
          date: data.date,
          allSegs: data.allSegs
        })
      } else {
        handleSetProp("moreEventsData", null)
      }
    },
    [handleSetProp]
  )

  const handleDeleteEvent = useCallback(
    (event: Event) => {
      const id = event?.id
      const type = event?.type?.toLowerCase()

      modal.confirm({
        centered: true,
        title: t("Confirm deleting"),
        content: t(
          `Are you sure you want to delete this ${type}? This action can NOT be undone.`
        ),
        cancelText: t("Cancel"),
        okText: t("Delete"),
        okButtonProps: { danger: true, ghost: true },
        onOk() {
          dispatch(deleteCalendarEvent({ id, type }))
        }
      })
    },
    [dispatch, modal, t]
  )

  const handleDoneEvent = useCallback(
    (e: Event, is_completed: boolean) => {
      dispatch(updateReminderStatus({ id: e?.id, type: e?.type, is_completed }))
    },
    [dispatch]
  )

  const handleCreateEvent = useCallback(
    (data: CalendarModalData) => {
      dispatch(createCalendarEvent(data))
    },
    [dispatch]
  )

  const handleUpdateEvent = useCallback(
    (data: Parameters<typeof partialUpdateCalendarEvent>[0]) => {
      dispatch(partialUpdateCalendarEvent(data))
    },
    [dispatch]
  )

  return (
    <>
      {fetching && <Spin fullscreen rootClassName={"z-[1001]"} spinning />}

      {addModalData && (
        <Suspense
          fallback={
            <Skeleton.Node
              active
              className="fixed z-10 inset-0 m-auto size-52"
            />
          }
        >
          <CalendarAddForm
            initialValues={addModalData}
            saveFetching={saveFetching}
            onClose={() => handleSetModalData(null)}
            onSubmit={handleCreateEvent}
            timezone={timezone}
          />
        </Suspense>
      )}

      {moreEventsData && (
        <Suspense
          fallback={
            <Skeleton.Node
              active
              className="fixed z-[1001] inset-0 m-auto size-52"
            />
          }
        >
          <CalendarMoreEventsModal
            dateFormat={dateFormat}
            timezone={timezone}
            permissions={permissions}
            data={moreEventsData}
            onClose={() => handleSetMoreEventsData()}
            onSelect={onSelectEvent}
          />
        </Suspense>
      )}

      {openingEventId && !selectedEvent && (
        <Modal
          open
          footer={null}
          closable={false}
          centered
          width={440}
          data-testid={"calendar-quickview-loading"}
        >
          <Skeleton active title paragraph={{ rows: 6 }} />
        </Modal>
      )}

      {failedEventId && (
        <Modal
          open
          footer={null}
          centered
          width={420}
          onCancel={onDismissEventError}
        >
          <Flex vertical align={"center"} gap={16} className={"py-6"}>
            <Text type={"danger"}>Unable to load event details.</Text>
            <Button type={"primary"} onClick={onRetryEventDetails}>
              Try Again
            </Button>
          </Flex>
        </Modal>
      )}

      {selectedEvent && (
        <Suspense
          fallback={
            <Skeleton.Node
              active
              className="fixed z-[1001] inset-0 m-auto size-52"
            />
          }
        >
          <CalendarView
            // Forces a fresh mount whenever the open event changes, so a
            // stale editMode/permission decision from the previous event
            // can never carry over onto this one.
            key={selectedEvent?.id}
            dateFormat={dateFormat}
            permissions={permissions}
            userId={userId}
            saveFetching={saveFetching}
            event={selectedEvent}
            timezone={timezone}
            startInEditMode={startInEditMode}
            onClose={() => onSelectEvent(null)}
            onEditCancelled={onEditCancelled}
            onDelete={handleDeleteEvent}
            onDone={handleDoneEvent}
            onSave={handleUpdateEvent}
            onInvited={refreshSelectedEvent}
          />
        </Suspense>
      )}
    </>
  )
}
